// Deterministic fuzzy search: subsequence match with position-aware scoring.
// No dependencies; stable ordering for reproducibility.

export interface Scored<T> {
  item: T;
  score: number;
}

/** Returns a 0..1 score for how well `query` fuzzy-matches `text`, or 0. */
export function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  const t = text.toLowerCase();
  if (!q) return 1;
  if (t.includes(q)) {
    // Contiguous substring: strong, boosted if it starts the string.
    const idx = t.indexOf(q);
    const startBonus = idx === 0 ? 0.15 : 0;
    return Math.min(1, 0.7 + (q.length / t.length) * 0.15 + startBonus);
  }
  // Subsequence match.
  let qi = 0;
  let gaps = 0;
  let lastPos = -1;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastPos >= 0 && ti - lastPos > 1) gaps++;
      lastPos = ti;
      qi++;
    }
  }
  if (qi < q.length) return 0;
  const density = q.length / (t.length || 1);
  const gapPenalty = gaps / q.length;
  return Math.max(0, 0.45 * density + 0.2 * (1 - gapPenalty));
}

export function search<T>(
  items: T[],
  query: string,
  keys: (item: T) => string[]
): Scored<T>[] {
  if (!query.trim()) return items.map((item) => ({ item, score: 1 }));
  const out: Scored<T>[] = [];
  for (const item of items) {
    let best = 0;
    for (const k of keys(item)) best = Math.max(best, fuzzyScore(query, k));
    if (best > 0) out.push({ item, score: best });
  }
  return out.sort((a, b) => b.score - a.score);
}
